/**
 * Keeping a caret in place when someone else's typing changes the text under
 * it (the script panel, PLAN §6.2 "Two people edit the same script"): the
 * change is located as the span between the common prefix and suffix of the
 * old and new text, and a caret after that span shifts by the length
 * difference; one inside it moves to the span's new end. Pure.
 */

export type Selection = { readonly start: number; readonly end: number };

function commonPrefix(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
}

function commonSuffix(a: string, b: string, prefix: number): number {
  let i = 0;
  while (i < a.length - prefix && i < b.length - prefix && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
}

function mapOffset(offset: number, prefix: number, oldEnd: number, delta: number): number {
  if (offset <= prefix) return offset;
  if (offset >= oldEnd) return offset + delta;
  return oldEnd + delta;
}

/**
 * The user's edit `prev → next`, replayed onto `current`.
 *
 * The textarea's input event reports the text the user typed into (`prev`,
 * what the DOM showed), the result (`next`) and where the caret ended up
 * (`caret`, the textarea's `selectionEnd`). If someone else's typing has
 * reached the store but not yet the DOM, `current` is newer than `prev`:
 * diffing `current → next` would read their characters as deleted by us
 * (found by the two-user script e2e test). Instead the one span the user
 * changed is located in `prev` (`editSpan`), mapped into `current`, and
 * applied there: past their insertion (`theirInsertion`), or — for any other
 * change of theirs — by `transformSelection`.
 */
export function rebaseEdit(prev: string, next: string, current: string, caret: number | null): string {
  if (current === prev) return next;
  const { prefix, suffix } = editSpan(prev, next, caret);
  const start = prefix;
  const end = prev.length - suffix;
  const inserted = next.slice(prefix, next.length - suffix);
  const theirs = theirInsertion(prev, current, start, end);
  if (theirs === null) {
    const span = transformSelection(prev, current, { start, end });
    return `${current.slice(0, span.start)}${inserted}${current.slice(Math.max(span.start, span.end))}`;
  }
  const { at, length } = theirs;
  // Their text is never inside what we remove: before it, after it, or — when they
  // typed into the very span we replaced — kept after ours (as Yjs keeps a concurrent insert).
  if (at <= start) return `${current.slice(0, start + length)}${inserted}${current.slice(end + length)}`;
  if (at >= end) return `${current.slice(0, start)}${inserted}${current.slice(end)}`;
  return `${current.slice(0, start)}${inserted}${current.slice(at, at + length)}${current.slice(end + length)}`;
}

/**
 * `current` read as `prev` with one insertion (null when it is not): where in
 * `prev` it went and how long it is. Like our own edit, theirs can often be read
 * at several places — every position from the longest common suffix's start to
 * the longest common prefix's end — and the reading chosen keeps it out of the
 * span `[start, end)` we replace when any reading can, so a deletion next to
 * their typing does not take it along.
 */
function theirInsertion(prev: string, current: string, start: number, end: number): { readonly at: number; readonly length: number } | null {
  const length = current.length - prev.length;
  if (length <= 0) return null;
  const latest = commonPrefix(prev, current);
  const earliest = prev.length - commonSuffix(prev, current, 0);
  if (earliest > latest) return null;
  if (start === end) return { at: latest, length };
  if (earliest <= start) return { at: Math.min(latest, start), length };
  if (latest >= end) return { at: Math.max(earliest, end), length };
  return { at: earliest, length };
}

/**
 * The one span `prev → next` changed, as the lengths of the text kept before
 * (`prefix`) and after it (`suffix`).
 *
 * The texts alone do not always say where an edit was: typing `"  ` at 2 in
 * `  " ` gives the same `  "  " ` as typing `  "` at the end, so the longest
 * common prefix reads it as the latter. That is harmless until the edit is
 * replayed onto text someone else changed inside the ambiguous run — their
 * `#` at 3 then lands before our insertion instead of after it, and ours no
 * longer reads as what we typed (fast-check seed -881009770 found it). The
 * caret is the tie-breaker: every single-span edit a textarea makes (typing,
 * a paste, a replaced selection, Backspace, Delete) leaves the caret at the
 * end of the text it inserted, so the edit is the span ending there. The
 * caret only CHOOSES among the readings the texts allow — the ones as narrow
 * as the longest-prefix one, ending no later than it and no earlier than the
 * longest common suffix lets — and never widens the span: an edit the caret
 * does not end (a spellcheck or autocorrect replacement of an earlier word,
 * a text substitution, a drag) would otherwise read every character between
 * the edit and the caret as retyped, moving their typing there or restoring
 * what they deleted. A caret outside that range, or none, falls back to the
 * longest common prefix.
 */
export function editSpan(prev: string, next: string, caret: number | null): { readonly prefix: number; readonly suffix: number } {
  const common = commonPrefix(prev, next);
  const shortest = commonSuffix(prev, next, common);
  const longest = commonSuffix(prev, next, 0);
  if (caret !== null && caret >= 0 && caret <= next.length) {
    const suffix = next.length - caret;
    if (suffix >= shortest && suffix <= longest) return { prefix: Math.min(common, caret, prev.length - suffix), suffix };
  }
  return { prefix: common, suffix: shortest };
}

/** Where `selection` in `before` belongs in `after`. */
export function transformSelection(before: string, after: string, selection: Selection): Selection {
  if (before === after) return selection;
  const prefix = commonPrefix(before, after);
  const suffix = commonSuffix(before, after, prefix);
  const oldEnd = before.length - suffix;
  const delta = after.length - before.length;
  return { start: mapOffset(selection.start, prefix, oldEnd, delta), end: mapOffset(selection.end, prefix, oldEnd, delta) };
}
